import { useState, useEffect, useRef, type ReactNode } from 'react';

interface Segment {
  text: string;
  color: string;
  typeSpeed: number; // ms per char, 0 = instant
}

interface Line {
  segments: Segment[];
  pauseAfter: number;
}

const lines: Line[] = [
  {
    segments: [
      { text: '$ quicktl init', color: 'text-slate-300', typeSpeed: 40 },
    ],
    pauseAfter: 500,
  },
  {
    segments: [
      { text: '✔ Select a project ', color: 'text-emerald-400', typeSpeed: 0 },
      { text: 'my-app', color: 'text-amber-300', typeSpeed: 35 },
      { text: ' (en → fr, es, de)', color: 'text-slate-500', typeSpeed: 0 },
    ],
    pauseAfter: 250,
  },
  {
    segments: [
      { text: '✔ Source locale file ', color: 'text-emerald-400', typeSpeed: 0 },
      { text: 'locales/en.json', color: 'text-amber-300', typeSpeed: 30 },
    ],
    pauseAfter: 250,
  },
  {
    segments: [
      { text: '✔ Target path pattern ', color: 'text-emerald-400', typeSpeed: 0 },
      { text: 'locales/{locale}.json', color: 'text-amber-300', typeSpeed: 30 },
    ],
    pauseAfter: 250,
  },
  {
    segments: [
      { text: '✓ Created .quicktl.json', color: 'text-emerald-400', typeSpeed: 0 },
    ],
    pauseAfter: 600,
  },
  {
    segments: [{ text: '', color: 'text-slate-300', typeSpeed: 0 }],
    pauseAfter: 0,
  },
  {
    segments: [
      { text: '$ quicktl translate', color: 'text-slate-300', typeSpeed: 40 },
    ],
    pauseAfter: 600,
  },
  {
    segments: [
      { text: '✔ Pushed 142 keys to main', color: 'text-emerald-400', typeSpeed: 0 },
    ],
    pauseAfter: 300,
  },
  {
    segments: [
      { text: '✔ Translating 142 strings...', color: 'text-emerald-400', typeSpeed: 0 },
    ],
    pauseAfter: 800,
  },
  {
    segments: [
      { text: '✔ Translated 142 strings (3 locales)', color: 'text-emerald-400', typeSpeed: 0 },
    ],
    pauseAfter: 200,
  },
  {
    segments: [
      { text: '✔ Downloaded 3 locale(s)', color: 'text-emerald-400', typeSpeed: 0 },
    ],
    pauseAfter: 150,
  },
  {
    segments: [
      { text: '  fr → locales/fr.json', color: 'text-slate-500', typeSpeed: 0 },
    ],
    pauseAfter: 100,
  },
  {
    segments: [
      { text: '  es → locales/es.json', color: 'text-slate-500', typeSpeed: 0 },
    ],
    pauseAfter: 100,
  },
  {
    segments: [
      { text: '  de → locales/de.json', color: 'text-slate-500', typeSpeed: 0 },
    ],
    pauseAfter: 500,
  },
  {
    segments: [{ text: '$ ', color: 'text-slate-300', typeSpeed: 0 }],
    pauseAfter: 0,
  },
];

function renderSegments(segments: Segment[]): ReactNode {
  return segments.map((seg, i) => (
    <span key={i} className={seg.color}>{seg.text}</span>
  ));
}

function renderAllLines(): ReactNode[] {
  return lines.map((line, i) => (
    <div key={i}>{renderSegments(line.segments)}</div>
  ));
}

const srText = lines.map((l) => l.segments.map((s) => s.text).join('')).join('\n');

function prefersReducedMotion() {
  return typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

interface DisplayedLine {
  segments: { text: string; color: string }[];
}

export default function TypingTerminal() {
  const reduced = prefersReducedMotion();
  const [completedLines, setCompletedLines] = useState<DisplayedLine[]>(
    reduced ? lines.map((l) => ({ segments: l.segments.map((s) => ({ text: s.text, color: s.color })) })) : [],
  );
  const [activeLine, setActiveLine] = useState<DisplayedLine | null>(null);
  const [showCursor, setShowCursor] = useState(!reduced);
  const hasStarted = useRef(false);

  useEffect(() => {
    if (hasStarted.current || reduced) return;
    hasStarted.current = true;

    let lineIdx = 0;
    const accumulated: DisplayedLine[] = [];

    function processLine() {
      if (lineIdx >= lines.length) {
        setShowCursor(false);
        return;
      }

      const line = lines[lineIdx];
      const segments = line.segments;
      let segIdx = 0;
      const completedSegs: { text: string; color: string }[] = [];

      function processSegment() {
        if (segIdx >= segments.length) {
          // Line complete — move to completed
          accumulated.push({ segments: [...completedSegs] });
          setCompletedLines([...accumulated]);
          setActiveLine(null);
          lineIdx++;
          setTimeout(processLine, line.pauseAfter);
          return;
        }

        const seg = segments[segIdx];

        if (seg.typeSpeed === 0) {
          // Instant segment
          completedSegs.push({ text: seg.text, color: seg.color });
          setActiveLine({ segments: [...completedSegs] });
          segIdx++;
          processSegment();
        } else {
          // Typed segment — character by character
          let charIdx = 0;

          function typeChar() {
            if (charIdx >= seg.text.length) {
              completedSegs.push({ text: seg.text, color: seg.color });
              segIdx++;
              processSegment();
              return;
            }

            charIdx++;
            const partial = { text: seg.text.slice(0, charIdx), color: seg.color };
            setActiveLine({ segments: [...completedSegs, partial] });
            setTimeout(typeChar, seg.typeSpeed);
          }

          typeChar();
        }
      }

      processSegment();
    }

    setTimeout(processLine, 800);
  }, [reduced]);

  return (
    <div
      dir="ltr"
      className="mx-auto mt-10 max-w-lg rounded-xl border border-slate-800/80 bg-slate-900/80 p-5 text-left font-mono text-sm"
      role="img"
      aria-label={`Terminal showing QuickTL CLI output: ${srText}`}
    >
      <div className="flex items-center gap-2 text-slate-500 mb-3" aria-hidden="true">
        <span className="h-3 w-3 rounded-full bg-rose-500/60" />
        <span className="h-3 w-3 rounded-full bg-amber-500/60" />
        <span className="h-3 w-3 rounded-full bg-emerald-500/60" />
      </div>
      <div className="min-h-[340px]" aria-hidden="true">
        {completedLines.map((line, i) => (
          <div key={i}>
            {line.segments.map((seg, j) => (
              <span key={j} className={seg.color}>{seg.text}</span>
            ))}
          </div>
        ))}
        {activeLine && (
          <div>
            {activeLine.segments.map((seg, j) => (
              <span key={j} className={seg.color}>{seg.text}</span>
            ))}
            {showCursor && <span className="typing-cursor text-emerald-400">▋</span>}
          </div>
        )}
        {!activeLine && showCursor && (
          <div>
            <span className="typing-cursor text-emerald-400">▋</span>
          </div>
        )}
      </div>
    </div>
  );
}
